<template>
    <div slot="grid-actions">
        <modal id="import-clients" ref="importClients" @hide="resetState">
            <template slot="title">{{ t('clientsImportClients-title') }}</template>
            <template slot="body">
                <div class="field">
                    <label
                        >{{ t('clientsImportClients-upload_label') }}</label
                    >
                    <input
                        type="file"
                        name="csv"
                        accept=".csv"
                        @change="upload"
                    />
                </div>
                <template v-if="step === 'loading'">
                    <span class="far loader fa-spinner fa-spin fa-3x"></span>
                    {{ t('clientsImportClients-loading_message') }}
                </template>
                <template v-show="step === 'upload' || step === 'preview'">
                    <template v-if="!isFileSizeValid">
                        <div class="alert alert-danger">
                            {{ t('clientsImportClients-file_size_error', [maxFileSize]) }}
                        </div>
                    </template>
                    <template v-if="csvErrors">
                        <div class="alert alert-danger">
                            {{ t('clientsImportClients-csv_error') }}
                        </div>
                    </template>
                    <div
                        class="field field-columns"
                        v-if="step === 'upload' && headerRow.length"
                    >
                        <table class="column-table table table-striped">
                            <tr
                                v-for="(col, id) in config.main_column"
                                v-show="
                                    col.show === undefined || col.show(config)
                                "
                                :key="id"
                            >
                                <td>
                                    <span
                                        :class="{
                                            required:
                                                typeof col.required ===
                                                'function'
                                                    ? col.required(config)
                                                    : col.required,
                                        }"
                                    >
                                        {{ columnLabels[id] }}
                                    </span>
                                </td>
                                <td>
                                    <select
                                        v-model="
                                            config.main_column[id][
                                                'mapped_column'
                                            ]
                                        "
                                        class="form-control"
                                    >
                                        <option value=""></option>
                                        <option
                                            v-for="(colCSV, index) in headerRow"
                                            :key="index"
                                        >
                                            {{ colCSV }}
                                        </option>
                                    </select>
                                </td>
                                <td class="extra">
                                    <div
                                        class="field-extra"
                                        v-if="
                                            col.extra.include_time !== undefined
                                        "
                                    >
                                        <div>
                                            <label
                                                ><input
                                                    type="checkbox"
                                                    v-model="
                                                        col.extra.include_time
                                                    "
                                                />
                                                {{ t('clientsImportClients-include_time') }}</label
                                            >
                                        </div>
                                    </div>
                                    <div
                                        class="field-extra"
                                        v-if="col.extra.format !== undefined"
                                    >
                                        <div><label>{{ t('clientsImportClients-format') }}</label></div>
                                        <div>
                                            <input
                                                class="form-control"
                                                type="text"
                                                v-model="col.extra.format"
                                            />
                                        </div>
                                    </div>
                                    <div
                                        class="field-extra"
                                        v-if="
                                            col.extra.decimal_separator !==
                                            undefined
                                        "
                                    >
                                        <div>
                                            <label>{{ t('clientsImportClients-decimal_separator') }}</label>
                                        </div>
                                        <div>
                                            <input
                                                class="form-control"
                                                type="text"
                                                v-model="
                                                    col.extra.decimal_separator
                                                "
                                                maxlength="1"
                                            />
                                        </div>
                                    </div>
                                    <div
                                        class="field-extra"
                                        v-if="
                                            col.extra.is_full_name !== undefined
                                        "
                                    >
                                        <div>
                                            <label
                                                ><input
                                                    type="checkbox"
                                                    v-model="
                                                        col.extra.is_full_name
                                                    "
                                                />
                                                {{ t('clientsImportClients-full_name') }}</label
                                            >
                                        </div>
                                    </div>
                                    <div
                                        class="field-extra"
                                        v-if="
                                            col.extra.default_prefix !==
                                            undefined
                                        "
                                    >
                                        <div><label>{{ t('clientsImportClients-default_prefix') }}</label></div>
                                        <div>
                                            <input
                                                class="form-control"
                                                type="text"
                                                v-model="
                                                    col.extra.default_prefix
                                                "
                                                maxlength="3"
                                            />
                                        </div>
                                    </div>
                                </td>
                            </tr>
                            <tr
                                v-for="(col, id) in config.custom_fields"
                                :key="id"
                            >
                                <td>{{ fieldLabel(id) }}</td>
                                <td>
                                    <select
                                        v-model="
                                            config.custom_fields[id][
                                                'mapped_column'
                                            ]
                                        "
                                        class="form-control"
                                    >
                                        <option value=""></option>
                                        <option
                                            v-for="(colCSV, index) in headerRow"
                                            :key="index"
                                        >
                                            {{ colCSV }}
                                        </option>
                                    </select>
                                </td>
                                <td>
                                    <div
                                        class="field-extra"
                                        v-if="
                                            col.extra.include_time !== undefined
                                        "
                                    >
                                        <div>
                                            <label
                                                ><input
                                                    type="checkbox"
                                                    v-model="
                                                        col.extra.include_time
                                                    "
                                                />
                                                {{ t('clientsImportClients-include_time') }}</label
                                            >
                                        </div>
                                    </div>
                                    <div
                                        class="field-extra"
                                        v-if="col.extra.format !== undefined"
                                    >
                                        <div><label>{{ t('clientsImportClients-format') }}</label></div>
                                        <div>
                                            <input
                                                class="form-control"
                                                type="text"
                                                v-model="col.extra.format"
                                            />
                                        </div>
                                    </div>
                                    <div
                                        class="field-extra"
                                        v-if="
                                            col.extra.decimal_separator !==
                                            undefined
                                        "
                                    >
                                        <div>
                                            <label>{{ t('clientsImportClients-decimal_separator') }}</label>
                                        </div>
                                        <div>
                                            <input
                                                class="form-control"
                                                type="text"
                                                v-model="
                                                    col.extra.decimal_separator
                                                "
                                                maxlength="1"
                                            />
                                        </div>
                                    </div>
                                    <div
                                        class="field-extra"
                                        v-if="
                                            col.extra.is_full_name !== undefined
                                        "
                                    >
                                        <div>
                                            <label
                                                ><input
                                                    type="checkbox"
                                                    v-model="
                                                        col.extra.is_full_name
                                                    "
                                                />
                                                {{ t('clientsImportClients-full_name') }}</label
                                            >
                                        </div>
                                    </div>
                                    <div
                                        class="field-extra"
                                        v-if="
                                            col.extra.default_prefix !==
                                            undefined
                                        "
                                    >
                                        <div><label>{{ t('clientsImportClients-default_prefix') }}</label></div>
                                        <div>
                                            <input
                                                class="form-control"
                                                type="text"
                                                v-model="
                                                    col.extra.default_prefix
                                                "
                                                maxlength="3"
                                            />
                                        </div>
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td colspan="3">
                                    <div class="pt-2 pb-2">
                                        <label
                                            ><input
                                                type="checkbox"
                                                v-model="updateProfile"
                                            />
                                            {{ t('clientsImportClients-update_profiles') }}</label
                                        >
                                    </div>
                                </td>
                            </tr>
                        </table>
                    </div>
                    <div v-if="step === 'preview'">
                        <table class="table table-preview">
                            <thead>
                                <tr>
                                    <td
                                        v-for="(col, idx) in columnLabels"
                                        :key="idx"
                                    >
                                        {{ col }}
                                    </td>
                                    <td
                                        v-for="(field, index) in customFields"
                                        :key="index"
                                    >
                                        {{ field.field_name }}
                                    </td>
                                </tr>
                            </thead>
                            <tbody>
                                <tr
                                    v-for="(row, index) in preview"
                                    :key="index"
                                >
                                    <td
                                        v-for="(col, idx) in columnLabels"
                                        :key="idx"
                                    >
                                        {{ row[idx] }}
                                    </td>
                                    <td
                                        v-for="(field, index) in customFields"
                                        :key="index"
                                    >
                                        {{
                                            row["custom_fields"][field.id]
                                                ? row["custom_fields"][field.id]
                                                : ""
                                        }}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div v-if="step === 'queued'" class="text-center py-3">
                        <span class="far fa-check-circle fa-3x text-success"></span>
                        <p class="mt-3">{{ t('clientsImportClients-queued_message') }}</p>
                        <router-link to="/imports" @click.native="$refs.importClients.close()">
                            {{ t('clientsImportClients-queued_link') }}
                        </router-link>
                    </div>
                    <div v-if="step === 'success'">
                        <table class="table">
                            <tr>
                                <td>{{ t('clientsImportClients-summary-new_clients') }}</td>
                                <td>{{ newClients }}</td>
                            </tr>
                            <tr>
                                <td>{{ t('clientsImportClients-summary-updated_clients') }}</td>
                                <td>{{ updatedClients }}</td>
                            </tr>
                        </table>
                    </div>
                </template>
            </template>
            <template slot="footer">
                <template v-if="step === 'upload'">
                    <button
                        class="btn btn-success"
                        @click="
                            step = 'upload';
                            $refs.importClients.close();
                        "
                    >
                        {{ t('clientsImportClients-cancel') }}
                    </button>
                    <button
                        class="btn btn-success"
                        :disabled="!isValid"
                        @click="previewRows()"
                    >
                        {{ t('clientsImportClients-next') }}
                    </button>
                </template>
                <template v-if="step === 'preview'">
                    <button class="btn btn-success" @click="step = 'upload'">
                        {{ t('clientsImportClients-back') }}
                    </button>
                    <button
                        class="btn btn-success"
                        :disabled="!isValid"
                        @click="importClients()"
                    >
                        {{ t('clientsImportClients-import') }}
                    </button>
                </template>
                <template v-if="step === 'queued'">
                    <button
                        class="btn btn-success"
                        @click="
                            step = 'upload';
                            $refs.importClients.close();
                        "
                    >
                        {{ t('clientsImportClients-close') }}
                    </button>
                </template>
                <template v-if="step === 'success'">
                    <button
                        class="btn btn-success"
                        @click="
                            step = 'upload';
                            $refs.importClients.close();
                        "
                    >
                        {{ t('clientsImportClients-close') }}
                    </button>
                </template>
            </template>
        </modal>
        <slot>
            <button class="btn btn-success" @click="$refs.importClients.open()">
                {{ t('clientsImportClients-open_button') }}
            </button>
        </slot>
    </div>
</template>
<script>
import keviApi from "../keviApi.js";
import Papa from "papaparse";
export default {
    data() {
        return {
            step: "upload",
            file: null,
            maxFileSize: 50, //In MB
            rows: [],
            headerRow: [],
            csvErrors: false,
            parsed: false,
            result: {},
            preview: [],
            newClients: 0,
            updatedClients: 0,
            updateProfile: false,
            customFields: [],
            importHistoryId: null,
            config: {
                main_column: {
                    client_external_id: {
                        mapped_column: "",
                        required: function (columns) {
                            return (
                                columns.main_column.whatsapp.mapped_column ===
                                ""
                            );
                        },
                        extra: {},
                    },
                    first_name: {
                        mapped_column: "",
                        required: false,
                        extra: {
                            is_full_name: false,
                        },
                    },
                    last_name: {
                        mapped_column: "",
                        required: false,
                        extra: {},
                        show: function (columns) {
                            return !columns.main_column.first_name.extra
                                .is_full_name;
                        },
                    },
                    whatsapp: {
                        mapped_column: "",
                        required: function (columns) {
                            return (
                                columns.main_column.client_external_id
                                    .mapped_column === ""
                            );
                        },
                        extra: {
                            default_prefix: "",
                        },
                    },
                    email: {
                        mapped_column: "",
                        required: false,
                        extra: {},
                    },
                    birthdate: {
                        mapped_column: "",
                        required: true,
                        extra: {
                            format: "d/m/Y",
                        },
                    },
                },
                custom_fields: {},
            },
        };
    },
    computed: {
        columnLabels() {
            return {
                client_external_id: this.t("clientsImportClients-columns-client_external_id"),
                first_name: this.t("clientsImportClients-columns-first_name"),
                last_name: this.t("clientsImportClients-columns-last_name"),
                whatsapp: this.t("clientsImportClients-columns-whatsapp"),
                email: this.t("clientsImportClients-columns-email"),
                birthdate: this.t("clientsImportClients-columns-birthdate"),
            };
        },
        isFileSizeValid() {
            return this.file
                ? this.file.size <= this.maxFileSize * 1024 * 1024
                : true;
        },
        isValid() {
            return true;
            if (
                this.isFileSizeValid &&
                !this.csvErrors &&
                this.rows.length > 0
            ) {
                return true;
            }
            return false;
        },
    },
    mounted() {
      window.setTimeout(() => {
        this.loadFields();
      }, 300);
    },
    methods: {
        t: function (term, replaces) {
            return this.$root.t(term, replaces);
        },
        fieldLabel(id) {
            const field = this.customFields.find((f) => String(f.id) === String(id));
            return field ? field.field_name : "";
        },
        loadFields() {
            const options = keviApi.getOptions("client-fields", "GET");
            return this.$http(options).then((response) => {
                this.customFields = JSON.parse(JSON.stringify(response.body));
                this.config.custom_fields = {};
                this.customFields.forEach((field) => {
                    let extra = {};

                    if (field.type === "date") {
                        extra = {
                            format: "d/m/Y",
                        };
                    }
                    this.config.custom_fields[field.id] = {
                        mapped_column: "",
                        required: false,
                        extra: extra,
                    };
                });
            });
        },
        upload(file) {
            this.file = file.target.files[0] || null;
            if (this.isFileSizeValid) {
                this.prepareDemo();
            }
        },
        previewRows() {
            let options = keviApi.getOptions(
                "importer/preview-clients",
                "POST",
                {
                    rows: this.rows.slice(0, 20),
                    config: this.config,
                },
            );
            this.step = "loading";

            this.$http(options).then(function (response) {
                if (response.status === 200) {
                    this.step = "preview";
                    this.preview = response.body.preview;
                }
            });
        },
        importClients() {
            let columns = [];
            for (let c in this.config.main_column) {
                columns.push(this.config.main_column[c].mapped_column);
            }
            for (let co in this.config.custom_fields) {
                columns.push(this.config.custom_fields[co].mapped_column);
            }
            let cleanRow = (row) => {
                let strippedRow = {};
                columns.forEach((c) => {
                    if (c) {
                        strippedRow[c] = row[c];
                    }
                });
                return strippedRow;
            };
            let options = keviApi.getOptions(
                "importer/import-clients",
                "POST",
                {
                    rows: this.rows.map(cleanRow),
                    config: this.config,
                    updateProfile: this.updateProfile,
                    filename: this.file ? this.file.name : null,
                },
            );
            this.step = "loading";

            this.$http(options).then(function (response) {
                // Import runs in the background now; 202 means the job was queued.
                if (response.status === 202) {
                    this.step = "queued";
                    this.importHistoryId = response.body.import_history_id;
                    this.$emit("queued", this.importHistoryId);
                } else if (response.status === 200) {
                    this.step = "success";
                    this.newClients = response.body.newClients;
                    this.updatedClients = response.body.updatedClients;
                }
            });
        },
        prepareDemo() {
            this.csvErrors = false;
            this.headerRow = [];
            this.parsed = false;
            Papa.parse(this.file, {
                header: true,
                delimiter: "",
                skipEmptyLines: true,
                complete: (results) => {
                    if (results.errors.length > 0) {
                        this.csvErrors = true;
                        console.log(results.errors);
                    } else {
                        this.parsed = true;
                        this.headerRow = Object.keys(results.data[0]).sort();
                        this.rows = results.data;
                    }
                },
            });
        },
        download(filename, text) {
            const element = document.createElement("a");
            element.setAttribute(
                "href",
                "data:text/plain;charset=utf-8," + encodeURIComponent(text),
            );
            element.setAttribute("download", filename);

            element.style.display = "none";
            document.body.appendChild(element);

            element.click();

            document.body.removeChild(element);
        },
        downloadFailedRows() {
            const data = Papa.unparse(this.failedRows);
            this.download("error.csv", data);
        },
        resetState() {
            this.step = "upload";
            this.file = null;
            this.rows = [];
            this.headerRow = [];
            this.csvErrors = false;
            this.parsed = false;
            this.result = {};
            this.preview = [];
            this.newClients = 0;
            this.updatedClients = 0;
            this.updateProfile = false;
            this.importHistoryId = null;

            for (let c in this.config.main_column) {
                this.config.main_column[c].mapped_column = "";
            }
            for (let co in this.config.custom_fields) {
                this.config.custom_fields[co].mapped_column = "";
            }

            this.config.main_column.first_name.extra.is_full_name = false;
            this.config.main_column.whatsapp.extra.default_prefix = "";
            this.config.main_column.birthdate.extra.format = "d/m/Y";

            const fileInput = document.querySelector('#import-clients input[type="file"]');
            if (fileInput) {
                fileInput.value = '';
            }
        },
    },
    watch: {
        headerRow() {
            let clean = (c) => c.toLowerCase().replace(/[^A-z]/, "");
            let columns = this.headerRow.map(clean);
            let mappedHeader = {};
            for (let c in columns) {
                mappedHeader[columns[c]] = this.headerRow[c];
            }
            for (let c in this.config.main_column) {
                let cName = clean(this.columnLabels[c]);
                this.config.main_column[c].mapped_column =
                    mappedHeader[cName] || "";
            }
            for (let co in this.config.custom_fields) {
                let coName = clean(this.fieldLabel(co));
                this.config.custom_fields[co].mapped_column =
                    mappedHeader[coName] || "";
            }
        },
    },
};
</script>
<style scoped lang="scss">
.field {
    margin-top: 20px;

    label {
        font-size: 14px;
        font-weight: 500;
    }
}

table td {
    font-size: 12px;
    padding: 6px;
    vertical-align: middle;
    .required {
        font-weight: 600;
        &:after {
            content: "*";
        }
    }
    select,
    input {
        margin-top: 0;
    }
    &.extra {
        .field-extra {
            display: inline-block;
            margin-right: 20px;
            label {
                margin-bottom: 5px;
            }
        }
    }
}

.table-preview td {
    min-width: 150px;
}

::v-deep {
    .modal-dialog {
        max-width: 800px;
    }
}
</style>
